/**
 * Super Admin — Nettoyage : détection (dry-run), suppression individuelle
 * et purge des données de test. Protégés : Super Admins, compte démo du
 * guide, leads convertis.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Loader2, Search, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ title, count, children }) {
    return (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
            <div className="px-4 py-3 text-sm font-medium border-b border-border/60 flex items-center justify-between">
                <span>{title}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${count > 0 ? 'bg-secondary/15 text-secondary' : 'bg-surface-muted text-muted-foreground'}`}>{count}</span>
            </div>
            {count > 0 ? children : <div className="px-4 py-3 text-sm text-muted-foreground">Rien à nettoyer.</div>}
        </div>
    );
}

function Row({ main, sub, onDelete, testid }) {
    const [busy, setBusy] = useState(false);
    return (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border/40 last:border-0 text-sm" data-testid={testid}>
            <div>
                <div>{main}</div>
                {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
            </div>
            {onDelete && (
                <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={busy}
                    onClick={async () => { setBusy(true); await onDelete(); setBusy(false); }}
                    data-testid={`${testid}-delete`}>
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                </Button>
            )}
        </div>
    );
}

export default function CleanupAdmin({ auth }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirm, setConfirm] = useState('');
    const [purging, setPurging] = useState(false);
    const [purgeResult, setPurgeResult] = useState(null);

    const analyze = async () => {
        setLoading(true);
        setPurgeResult(null);
        try {
            const r = await axios.get(`${API}/super-admin/cleanup/preview`, auth);
            setPreview(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Analyse échouée');
        } finally { setLoading(false); }
    };

    const del = (url, label) => async () => {
        try {
            await axios.delete(url, auth);
            toast.success(`${label} supprimé`);
            analyze();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Suppression échouée');
        }
    };

    const purge = async () => {
        setPurging(true);
        try {
            const r = await axios.post(`${API}/super-admin/cleanup/purge`, { confirm }, auth);
            setPurgeResult(r.data.result);
            setConfirm('');
            toast.success('Purge terminée');
            analyze();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Purge échouée');
        } finally { setPurging(false); }
    };

    const total = preview ? Object.values(preview.totals).reduce((a, b) => a + b, 0) : 0;

    return (
        <div className="space-y-5" data-testid="sa-cleanup-admin">
            <div className="flex items-start gap-3 rounded-xl border border-border bg-surface-muted/40 p-4 text-sm">
                <ShieldCheck size={18} className="text-primary mt-0.5 shrink-0" />
                <div className="text-foreground/80">
                    Détection automatique des données de test (emails <code>@example.com</code>, préfixes <code>qa-</code>, <code>test-</code>, <code>demo-</code>, <code>e2e-</code>…).
                    Rien n'est supprimé sans votre confirmation. <strong>Toujours protégés</strong> : comptes Super Admin, compte démo du guide (guide.demo@testmail.dev), leads convertis.
                </div>
            </div>

            <Button onClick={analyze} disabled={loading} data-testid="sa-cleanup-analyze">
                {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Search size={14} className="mr-2" />}
                Analyser les données de test (aperçu)
            </Button>

            {purgeResult && (
                <div className="rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm" data-testid="sa-cleanup-result">
                    <div className="font-medium mb-1">Purge effectuée :</div>
                    <ul className="space-y-0.5 text-foreground/80">
                        <li>Espaces supprimés : {purgeResult.workspaces.deleted} {purgeResult.workspaces.skipped.length > 0 && `(${purgeResult.workspaces.skipped.length} ignorés)`}</li>
                        <li>Utilisateurs supprimés : {purgeResult.users.deleted} {purgeResult.users.skipped.length > 0 && `(${purgeResult.users.skipped.length} ignorés)`}</li>
                        <li>Leads CRM supprimés : {purgeResult.access_requests.deleted} {purgeResult.access_requests.skipped.length > 0 && `(${purgeResult.access_requests.skipped.length} ignorés)`}</li>
                        <li>Project Scores supprimés : {purgeResult.project_scores}</li>
                        <li>Événements analytics supprimés : {purgeResult.funnel_events}</li>
                    </ul>
                </div>
            )}

            {preview && (
                <>
                    <div className="grid gap-4">
                        <Section title="Utilisateurs de test" count={preview.totals.users}>
                            {preview.users.map(u => (
                                <Row key={u.id} main={u.email} sub={u.full_name}
                                    onDelete={del(`${API}/super-admin/users/${u.id}`, u.email)}
                                    testid={`sa-cleanup-user-${u.id}`} />
                            ))}
                        </Section>
                        <Section title="Espaces de travail de test" count={preview.totals.workspaces}>
                            {preview.workspaces.map(w => (
                                <Row key={w.id} main={w.name} sub={`créé le ${(w.created_at || '').slice(0, 10)}`}
                                    onDelete={del(`${API}/super-admin/workspaces/${w.id}`, w.name)}
                                    testid={`sa-cleanup-ws-${w.id}`} />
                            ))}
                        </Section>
                        <Section title="Leads CRM de test" count={preview.totals.access_requests}>
                            {preview.access_requests.map(l => (
                                <Row key={l.id} main={l.email} sub={`${l.full_name || ''} · ${l.stage}`}
                                    onDelete={l.stage === 'converted' ? null : del(`${API}/access-requests/${l.id}`, l.email)}
                                    testid={`sa-cleanup-lead-${l.id}`} />
                            ))}
                        </Section>
                        <Section title="Project Scores de test" count={preview.totals.project_scores}>
                            {preview.project_scores.map(s => (
                                <Row key={s.id} main={`${s.email} — ${s.total}/100`} sub={s.project_name}
                                    onDelete={del(`${API}/super-admin/project-scores/${s.id}`, 'Score')}
                                    testid={`sa-cleanup-score-${s.id}`} />
                            ))}
                        </Section>
                        <Section title="Événements analytics de test" count={preview.totals.funnel_events}>
                            <div className="px-4 py-3 text-sm text-muted-foreground">
                                {preview.funnel_events.count} événement(s) — identifiants : {preview.funnel_events.anon_ids.join(', ') || '—'}
                                <div className="text-[11px] mt-1">Supprimés en bloc lors de la purge.</div>
                            </div>
                        </Section>
                    </div>

                    {total > 0 ? (
                        <div className="rounded-xl border-2 border-destructive/40 bg-destructive/5 p-5 space-y-3" data-testid="sa-cleanup-purge-zone">
                            <div className="flex items-center gap-2 text-sm font-medium text-destructive">
                                <AlertTriangle size={16} /> Purge définitive de {total} élément(s) détecté(s)
                            </div>
                            <p className="text-sm text-foreground/70">
                                Cette action est irréversible. Tapez <strong>PURGER</strong> pour confirmer. La détection est refaite côté serveur au moment de la purge.
                            </p>
                            <div className="flex gap-3">
                                <Input value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                    placeholder="Tapez PURGER" className="h-10 w-44"
                                    data-testid="sa-cleanup-confirm-input" />
                                <Button variant="destructive" disabled={confirm !== 'PURGER' || purging} onClick={purge}
                                    data-testid="sa-cleanup-purge-btn">
                                    {purging ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                                    Purger les données de test
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="rounded-xl border border-border bg-white p-4 text-sm text-muted-foreground" data-testid="sa-cleanup-clean">
                            ✅ Aucune donnée de test détectée — la base est propre.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
